import { useCallback, useEffect, useRef, useState } from "react";

import {
  type EmailPngExportRunQuery,
  EmailPngExportRunStatus,
  type ExportEmailToPngPersonalizationInput,
  useEmailPngExportRunQuery,
  useExportEmailToPngMutation,
  useExportEmailToPsdMutation,
} from "src/graphql";
import {
  buildEmailLayeredBlob,
  type LayeredFormat,
} from "src/pages/ad-studio/psd-export/build-email-layered";
import { useGovernedRefetchInterval } from "src/hooks/use-governed-refetch-interval";
import {
  downloadBlob,
  downloadBlobZip,
  sanitizeFilename,
} from "src/utils/download";

export type EmailExportFormat = "png" | LayeredFormat;

/** What one render run delivers: PNG frames, or layered files in each listed format (they share one layers run). */
type EmailRunFormats = "png" | readonly LayeredFormat[];

export function isLayeredFormat(format: string): format is LayeredFormat {
  return format === "psd" || format === "idml";
}

const LIST_FORMAT = new Intl.ListFormat("en", { type: "conjunction" });

/** Joins format names for a toast or title, e.g. "PSD and IDML". */
export function formatList(formats: readonly string[]): string {
  return LIST_FORMAT.format(formats.map((format) => format.toUpperCase()));
}

const LAYERED_FORMAT_APP: Record<LayeredFormat, string> = {
  psd: "Photoshop",
  idml: "InDesign",
};

/** Names the apps that open these layered formats, e.g. "Photoshop and InDesign". */
export function formatApps(formats: readonly LayeredFormat[]): string {
  return LIST_FORMAT.format(
    formats.map((format) => LAYERED_FORMAT_APP[format]),
  );
}

/** Success toast title, e.g. "Downloaded 2 PSDs" or "Downloaded PSD and IDML". */
export function downloadedTitle(formats: readonly string[], count: number) {
  const list = formatList(formats);
  return count > 1 && formats.length === 1
    ? `Downloaded ${count} ${list}s`
    : `Downloaded ${list}`;
}

export type FrameWidth = "desktop" | "mobile";
export type FrameScheme = "light" | "dark";

export type FrameSelection = {
  width: FrameWidth;
  scheme: FrameScheme;
};

export type ExportResult = (
  | { kind: "clipboard" }
  | { kind: "download"; reason?: string; count: number }
) & {
  /**
   * Set when personalization was requested but the backend fell back to the
   * raw template (in whole or in part) instead of failing the export.
   */
  personalizationWarning?: string | null;
};

export type ExportMode = "clipboard" | "download";

type ExportOpts = {
  contentEmailId: string;
  contentEmailVersionId: string;
  filename?: string;
  frames?: FrameSelection[];
  mode: ExportMode;
  personalization?: ExportEmailToPngPersonalizationInput | null;
  /** Dynamic-content variant selections (slot id → branch id) from the
   * preview canvas; unselected slots resolve to their priority-first branch
   * server-side. */
  slotSelections?: Record<string, string> | null;
  /** Expand provider content blocks into their content, as the preview does.
   * Omitted means expand — only sent when the user opted out. */
  expandContentBlocks?: boolean;
};

type RenderedFrame = {
  width: FrameWidth;
  scheme: FrameScheme;
  blob: Blob;
};

type RunData = NonNullable<EmailPngExportRunQuery["emailPngExportRun"]>;

const POLL_BASE_MS = 1500;
const POLL_MAX_MS = 8000;

// Overall deadline for one export, from enqueue to a terminal run status.
// Renders normally finish in seconds; this is defense-in-depth against a
// run that never reaches a terminal state (worker crash, lost queue message)
// so the export dialog can't spin forever.
const EXPORT_TIMEOUT_MS = 3 * 60 * 1000;
export const PSD_EXPORT_TIMEOUT_MS = 10 * 60 * 1000;

// The URL points at our own API (GET /api/agents/files?...&stream=true), which
// is cross-origin from the app and authenticated by the session cookie — hence
// credentials: "include" (same pattern as the GraphQL endpoint). The timeout is
// defense-in-depth: if a proxy still stalls the connection, the fetch aborts
// and surfaces an error instead of leaving the export dialog spinning forever.
const PNG_FETCH_TIMEOUT_MS = 60_000;

async function fetchExportBlob(url: string): Promise<Blob> {
  const res = await fetch(url, {
    // The URL points at our own API, which sets Access-Control-Allow-Credentials
    // for the app origin, so we send the session cookie. (mode defaults to "cors"
    // for cross-origin requests, so it's left implicit.)
    credentials: "include",
    signal: AbortSignal.timeout(PNG_FETCH_TIMEOUT_MS),
  });
  if (!res.ok) {
    throw new Error(`Failed to download export (${res.status})`);
  }
  return res.blob();
}

async function copyBlobToClipboard(blob: Blob): Promise<void> {
  if (typeof ClipboardItem === "undefined" || !navigator.clipboard?.write) {
    throw new Error("Clipboard image write is not supported in this browser");
  }
  // ClipboardItem validates that the blob's MIME type matches the key.
  // S3 may return PNG bytes with a generic content-type (e.g. binary/octet-stream),
  // so re-wrap with the correct type before writing.
  const pngBlob =
    blob.type === "image/png" ? blob : new Blob([blob], { type: "image/png" });
  await navigator.clipboard.write([
    new ClipboardItem({ "image/png": pngBlob }),
  ]);
}

function buildFilename(base: string, frame: RenderedFrame): string {
  const stem = base.replace(/\.png$/i, "");
  return `${stem}-${frame.width}-${frame.scheme}.png`;
}

type ExportFile = { filename: string; blob: Blob };

function filenameStem(filename: string) {
  return (
    sanitizeFilename(filename.replace(/\.(png|psd|idml|zip)$/i, "")) ||
    "Untitled"
  );
}

/** Builds each layers artifact into a file per format, fetching each artifact once. */
export async function buildLayerFiles(
  frames: { suffix: string; url: string }[],
  stem: string,
  formats: readonly LayeredFormat[],
): Promise<ExportFile[]> {
  const files: ExportFile[] = [];
  // Assemble one viewport at a time to bound canvas memory on the user's device.
  for (const frame of frames) {
    const downloaded = await fetchExportBlob(frame.url);
    const artifact: unknown = JSON.parse(await downloaded.text());
    for (const format of formats)
      files.push({
        filename: `${stem}-${frame.suffix}.${format}`,
        blob: await buildEmailLayeredBlob(artifact, format),
      });
  }
  return files;
}

/** Builds each layers artifact into layered files and downloads them, zipped when there are several. */
export async function downloadLayerFiles(
  frames: { suffix: string; url: string }[],
  filename: string,
  formats: readonly LayeredFormat[],
): Promise<number> {
  const stem = filenameStem(filename);
  const files = await buildLayerFiles(frames, stem, formats);
  const first = files[0];
  if (!first) throw new Error("No layered files returned");
  if (files.length === 1) downloadBlob(first.blob, first.filename);
  else await downloadBlobZip(files, `${stem}.zip`);
  return files.length;
}

export async function deliverEmailExportFrames(
  run: RunData,
  mode: ExportMode,
  filename: string,
  formats: EmailRunFormats,
): Promise<ExportResult> {
  // url can be null if the in pending/running/failed. Filter  any
  // frames that don't have a URL.
  const readyFrames = run.frames.filter(
    (f): f is typeof f & { url: string } => f.url !== null,
  );

  if (readyFrames.length === 0 || readyFrames.length !== run.frames.length) {
    throw new Error("No frames returned");
  }

  const personalizationWarning = run.personalizationWarning;

  if (formats !== "png") {
    const count = await downloadLayerFiles(
      readyFrames.map((frame) => ({ suffix: frame.width, url: frame.url })),
      filename,
      formats,
    );
    return { kind: "download", count, personalizationWarning };
  }

  const frames: RenderedFrame[] = await Promise.all(
    readyFrames.map(async (frame) => ({
      width: frame.width,
      scheme: frame.scheme,
      blob: await fetchExportBlob(frame.url),
    })),
  );

  if (mode === "download") {
    for (const frame of frames) {
      const name =
        frames.length === 1 ? filename : buildFilename(filename, frame);
      downloadBlob(frame.blob, name);
    }
    return { kind: "download", count: frames.length, personalizationWarning };
  }

  if (frames.length > 1) {
    throw new Error(
      "Clipboard copy supports a single frame only. Pick one frame or use Download.",
    );
  }

  try {
    await copyBlobToClipboard(frames[0]!.blob);
    return { kind: "clipboard", personalizationWarning };
  } catch (err) {
    const reason = err instanceof Error ? err.message : "Unknown error";
    downloadBlob(frames[0]!.blob, filename);
    return { kind: "download", reason, count: 1, personalizationWarning };
  }
}

type PendingExport = {
  formats: EmailRunFormats;
  mode: ExportMode;
  filename: string;
  resolve: (result: ExportResult) => void;
  reject: (err: Error) => void;
};

/**
 * Kicks off a background PNG export run, polls for completion, then either
 * downloads each rendered frame or copies a single frame to the clipboard.
 */
export function useExportEmailFrames(formats: EmailRunFormats): {
  exportEmailFrames: (opts: ExportOpts) => Promise<ExportResult>;
  isLoading: boolean;
} {
  const { mutateAsync, isLoading: isEnqueueing } =
    useExportEmailToPngMutation();
  const { mutateAsync: exportPsd, isLoading: isEnqueueingPsd } =
    useExportEmailToPsdMutation();
  const [runId, setRunId] = useState<string | null>(null);
  const [isAssembling, setIsAssembling] = useState(false);
  const assemblingRef = useRef(false);
  const pendingRef = useRef<PendingExport | null>(null);
  const deadlineRef = useRef<number | null>(null);

  const clearDeadline = useCallback(() => {
    if (deadlineRef.current !== null) {
      window.clearTimeout(deadlineRef.current);
      deadlineRef.current = null;
    }
  }, []);

  // The deadline timer must not outlive the component (it would reject a
  // pending promise nobody holds anymore).
  useEffect(() => clearDeadline, [clearDeadline]);

  const { refetchInterval, resetBackoff } = useGovernedRefetchInterval<
    EmailPngExportRunQuery | undefined
  >({
    baseMs: POLL_BASE_MS,
    maxMs: POLL_MAX_MS,
    compareKey: (data) => [
      data?.emailPngExportRun?.status,
      data?.emailPngExportRun?.frames.length,
    ],
  });

  const finishPending = useCallback(
    (run: RunData) => {
      const pending = pendingRef.current;
      if (!pending) return;
      pendingRef.current = null;
      clearDeadline();
      setRunId(null);

      assemblingRef.current = true;
      setIsAssembling(true);
      deliverEmailExportFrames(
        run,
        pending.mode,
        pending.filename,
        pending.formats,
      )
        .then(pending.resolve)
        .catch(pending.reject)
        .finally(() => {
          assemblingRef.current = false;
          setIsAssembling(false);
        });
    },
    [clearDeadline],
  );

  const failPending = useCallback(
    (message: string) => {
      const pending = pendingRef.current;
      if (!pending) return;
      pendingRef.current = null;
      clearDeadline();
      setRunId(null);
      pending.reject(new Error(message));
    },
    [clearDeadline],
  );

  const { data: pollData } = useEmailPngExportRunQuery(
    { id: runId ?? "" },
    {
      enabled: runId !== null,
      refetchInterval: runId !== null ? refetchInterval : false,
      onError: (err: unknown) => {
        const message =
          err instanceof Error ? err.message : "Failed to fetch export run";
        failPending(message);
      },
    },
  );

  useEffect(() => {
    if (runId === null) return;
    const run = pollData?.emailPngExportRun;
    if (!run) return;

    if (run.status === EmailPngExportRunStatus.Complete) {
      finishPending(run);
    } else if (run.status === EmailPngExportRunStatus.Failed) {
      failPending(run.error ?? "Export failed");
    }
  }, [runId, pollData, finishPending, failPending]);

  const exportEmailFrames = useCallback(
    async (opts: ExportOpts): Promise<ExportResult> => {
      if (pendingRef.current || assemblingRef.current) {
        throw new Error("An export is already in progress");
      }

      const filename = opts.filename ?? "email-export.png";

      return new Promise<ExportResult>((resolve, reject) => {
        // Claim the slot synchronously so a rapid second call rejects
        // immediately instead of racing through `mutateAsync`.
        pendingRef.current = {
          formats,
          mode: opts.mode,
          filename,
          resolve,
          reject,
        };
        deadlineRef.current = window.setTimeout(
          () => {
            failPending(
              formats !== "png"
                ? "The export is taking longer than expected. Try again, or contact support if this keeps happening."
                : "Export timed out. Please try again, and contact support if the problem persists.",
            );
          },
          formats !== "png" ? PSD_EXPORT_TIMEOUT_MS : EXPORT_TIMEOUT_MS,
        );

        const enqueue = async () => {
          const variables = {
            input: {
              contentEmailId: opts.contentEmailId,
              contentEmailVersionId: opts.contentEmailVersionId,
              frames: opts.frames,
              personalization: opts.personalization,
              slotSelections: opts.slotSelections,
              expandContentBlocks: opts.expandContentBlocks,
            },
          };
          return formats !== "png"
            ? (await exportPsd(variables)).exportEmailToPsd
            : (await mutateAsync(variables)).exportEmailToPng;
        };
        enqueue()
          .then((enqueued) => {
            // The timeout may have already fired and cleared the pending
            // export; don't restart polling for an export nobody is awaiting.
            if (!pendingRef.current) return;
            resetBackoff();
            setRunId(enqueued.runId);
          })
          .catch((err: unknown) => {
            pendingRef.current = null;
            clearDeadline();
            reject(err instanceof Error ? err : new Error(String(err)));
          });
      });
    },
    [mutateAsync, exportPsd, formats, resetBackoff, failPending, clearDeadline],
  );

  return {
    exportEmailFrames,
    isLoading:
      isEnqueueing || isEnqueueingPsd || runId !== null || isAssembling,
  };
}

const PNG_FRAMES: FrameSelection[] = [
  { width: "desktop", scheme: "light" },
  { width: "desktop", scheme: "dark" },
  { width: "mobile", scheme: "light" },
  { width: "mobile", scheme: "dark" },
];

// Layered exports have no dark mode.
const LAYERED_FRAMES: FrameSelection[] = [
  { width: "desktop", scheme: "light" },
  { width: "mobile", scheme: "light" },
];

/**
 * Renders one email at every width (and scheme, for PNG) in each format and returns the files
 * instead of downloading them, so a bulk download can zip many emails together.
 * PNG renders in its own run, and every layered format builds from one shared layers run.
 */
export async function renderEmailExportFiles({
  contentEmailId,
  contentEmailVersionId,
  formats,
  filename,
  isCancelled,
}: {
  contentEmailId: string;
  contentEmailVersionId: string;
  formats: readonly EmailExportFormat[];
  filename: string;
  isCancelled: () => boolean;
}): Promise<{
  files: ExportFile[];
  personalizationWarnings: string[];
  /** Runs that failed, so the caller can name the formats it couldn't export while keeping the rest. */
  failures: { formats: readonly EmailExportFormat[]; error: unknown }[];
}> {
  const layered = formats.filter(isLayeredFormat);
  const runs: EmailRunFormats[] = [];
  if (formats.includes("png")) runs.push("png");
  if (layered.length > 0) runs.push(layered);
  const stem = filenameStem(filename);
  const files: ExportFile[] = [];
  const personalizationWarnings: string[] = [];
  const failures: { formats: readonly EmailExportFormat[]; error: unknown }[] =
    [];
  for (const run of runs) {
    if (isCancelled()) break;
    try {
      const { files: rendered, personalizationWarning } = await renderEmailRun({
        contentEmailId,
        contentEmailVersionId,
        formats: run,
        stem,
        isCancelled,
      });
      files.push(...rendered);
      if (personalizationWarning)
        personalizationWarnings.push(personalizationWarning);
    } catch (error) {
      failures.push({ formats: run === "png" ? ["png"] : run, error });
    }
  }
  return { files, personalizationWarnings, failures };
}

/** Polls in a loop rather than through a query, since a bulk run renders emails one after another. */
async function renderEmailRun({
  contentEmailId,
  contentEmailVersionId,
  formats,
  stem,
  isCancelled,
}: {
  contentEmailId: string;
  contentEmailVersionId: string;
  formats: EmailRunFormats;
  stem: string;
  isCancelled: () => boolean;
}): Promise<{ files: ExportFile[]; personalizationWarning: string | null }> {
  const variables = {
    input: {
      contentEmailId,
      contentEmailVersionId,
      frames: formats !== "png" ? LAYERED_FRAMES : PNG_FRAMES,
    },
  };
  const { runId } =
    formats !== "png"
      ? (await useExportEmailToPsdMutation.fetcher(variables)())
          .exportEmailToPsd
      : (await useExportEmailToPngMutation.fetcher(variables)())
          .exportEmailToPng;
  const run = await pollExportRun({
    fetchRun: async () =>
      (await useEmailPngExportRunQuery.fetcher({ id: runId })())
        .emailPngExportRun,
    timeoutMs: formats !== "png" ? PSD_EXPORT_TIMEOUT_MS : EXPORT_TIMEOUT_MS,
    isCancelled,
  });
  if (!run) return { files: [], personalizationWarning: null };
  const frames = run.frames.filter(
    (frame): frame is typeof frame & { url: string } => frame.url !== null,
  );
  if (frames.length === 0 || frames.length !== run.frames.length)
    throw new Error("No frames returned");
  const files =
    formats !== "png"
      ? await buildLayerFiles(
          frames.map((frame) => ({ suffix: frame.width, url: frame.url })),
          stem,
          formats,
        )
      : await Promise.all(
          frames.map(async (frame) => ({
            filename: `${stem}-${frame.width}-${frame.scheme}.png`,
            blob: await fetchExportBlob(frame.url),
          })),
        );
  return { files, personalizationWarning: run.personalizationWarning };
}

/** Polls a render run with backoff until it completes; resolves null when cancelled. */
export async function pollExportRun<
  Run extends { status: EmailPngExportRunStatus; error: string | null },
>({
  fetchRun,
  timeoutMs,
  isCancelled,
}: {
  fetchRun: () => Promise<Run | null>;
  timeoutMs: number;
  isCancelled: () => boolean;
}): Promise<Run | null> {
  const deadline = Date.now() + timeoutMs;
  let delay = POLL_BASE_MS;
  for (;;) {
    await new Promise((resolve) => window.setTimeout(resolve, delay));
    if (isCancelled()) return null;
    delay = Math.min(delay * 2, POLL_MAX_MS);
    const run = await fetchRun();
    if (run?.status === EmailPngExportRunStatus.Failed)
      throw new Error(run.error ?? "Export failed");
    if (run?.status === EmailPngExportRunStatus.Complete) return run;
    if (Date.now() > deadline)
      throw new Error("The export is taking longer than expected.");
  }
}

export function useExportEmailToPng() {
  const { exportEmailFrames, isLoading } = useExportEmailFrames("png");
  return { exportEmailToPng: exportEmailFrames, isLoading };
}
